import React, { useMemo, useState } from "react";
import { Input } from "antd";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Segmented } from "../../../../design-system";
import { PageHeader } from "../components/PageHeader";
import { Banner } from "../components/Banner";
import { StatusTag } from "../components/StatusTag";
import { useTailSpend } from "../TailSpendContext";
import {
    getActiveDeals,
    getBannerKpis,
    getClosedDeals,
    getNeedsAttentionDeals,
    getUpcomingNegotiations,
} from "../model/api";
import { DealState, stageKindLabel } from "../model/lifecycle";
import {
    Deal,
    DecisionResolution,
    Tone,
    Workspace,
} from "../model/types";
import { StageDiagram } from "./StageDiagram";
import classes from "./shared.module.less";

type View = "upcoming" | "attention" | "active" | "decisions" | "closed" | "all";

const dealPath = (id: string) => `/superadmin/demo/tail-spend/negotiations/${id}`;

function stageTone(state: DealState): Tone {
    switch (state.kind) {
        case "Triage":
            return "slate";
        case "Negotiating":
        case "AwaitingConfirmation":
            return "teal";
        case "Escalated":
            return "red";
        case "Held":
            return "amber";
        case "Executing":
            return "blue";
        case "ClosedWon":
            return "green";
        case "Walked":
        case "Reassigned":
            return "slate";
    }
}

export const NegotiationsView: React.FC = () => {
    const { workspace } = useTailSpend();
    const navigate = useNavigate();
    const [view, setView] = useState<View>("attention");
    const [search, setSearch] = useState("");
    const [diagramOpen, setDiagramOpen] = useState(false);

    const needs = useMemo(() => getNeedsAttentionDeals(workspace), [workspace]);
    const active = useMemo(() => getActiveDeals(workspace), [workspace]);
    const closed = useMemo(() => getClosedDeals(workspace), [workspace]);
    const upcoming = useMemo(
        () => getUpcomingNegotiations(workspace),
        [workspace]
    );
    const allCount = workspace.deals.length;
    const firstName = workspace.currentUser.name.split(" ")[0];
    const recentDecisions = workspace.decisionHistory.slice(0, 5);

    const dealSource =
        view === "upcoming"
            ? upcoming
            : view === "attention"
              ? needs
              : view === "active"
                ? active
                : view === "closed"
                  ? closed
                  : workspace.deals;

    const filteredDeals = useMemo(() => {
        if (view === "decisions") return [] as Deal[];
        const q = search.trim().toLowerCase();
        if (!q) return dealSource;
        return dealSource.filter(
            (d) =>
                d.supplierName.toLowerCase().includes(q) ||
                d.categoryName.toLowerCase().includes(q)
        );
    }, [dealSource, search, view]);

    const filteredDecisions = useMemo(() => {
        if (view !== "decisions") return [] as DecisionResolution[];
        const q = search.trim().toLowerCase();
        if (!q) return recentDecisions;
        return recentDecisions.filter(
            (d) =>
                d.supplierName.toLowerCase().includes(q) ||
                d.issue.toLowerCase().includes(q) ||
                d.chosenOptionTitle.toLowerCase().includes(q)
        );
    }, [recentDecisions, search, view]);

    const isEmpty =
        view === "decisions"
            ? filteredDecisions.length === 0
            : filteredDeals.length === 0;

    return (
        <>
            <PageHeader
                title="Negotiations"
                subtitle="Every deal the agents are working. Start with the queue that needs you, then look at what's running on its own."
            />

            <div className={classes.banner}>
                <Banner
                    headline={
                        <>
                            Hi {firstName}, the agents have captured{" "}
                            <span style={{ color: "#5ad81d", fontWeight: 700 }}>
                                $2.4M
                            </span>{" "}
                            YTD across{" "}
                            {workspace.suppliersInScope.toLocaleString()} suppliers
                            —{" "}
                            {needs.length === 0
                                ? "nothing needs you right now."
                                : `${needs.length} ${
                                      needs.length === 1 ? "deal needs" : "deals need"
                                  } you.`}
                        </>
                    }
                    kpis={getBannerKpis(workspace)}
                    alertIndex={2}
                />
            </div>

            <div data-tour-id="negotiationsTable">
                <div className={classes.toolbar}>
                    <Segmented
                        value={view}
                        onChange={(v) => setView(v as View)}
                        options={[
                            { label: `Upcoming (${upcoming.length})`, value: "upcoming" },
                            { label: `Active (${active.length})`, value: "active" },
                            { label: `Needs attention (${needs.length})`, value: "attention" },
                            { label: `Recent decisions (${recentDecisions.length})`, value: "decisions" },
                            { label: `Recently closed (${closed.length})`, value: "closed" },
                            { label: `All (${allCount})`, value: "all" },
                        ]}
                    />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Filter…"
                        prefix={<SearchOutlined />}
                        className={classes.filterInput}
                        style={{ width: 220 }}
                    />
                </div>

                {isEmpty ? (
                    <EmptyState view={view} />
                ) : view === "decisions" ? (
                    <DecisionsTable
                        decisions={filteredDecisions}
                        workspace={workspace}
                        onOpen={(id) => navigate(dealPath(id))}
                    />
                ) : (
                    <table className={`${classes.activeTable} ${classes.sixColTable}`}>
                        <thead>
                            <tr>
                                <th>Supplier</th>
                                <th>Category</th>
                                <th>Annual</th>
                                <th>
                                    <span className={classes.stageHeader}>
                                        Stage
                                        <button
                                            type="button"
                                            className={classes.stageInfoBtn}
                                            onClick={() => setDiagramOpen(true)}
                                            aria-label="Show stage diagram"
                                        >
                                            <InfoCircleOutlined />
                                        </button>
                                    </span>
                                </th>
                                <th>Status</th>
                                <th>
                                    {view === "upcoming" ? "When" : "Result"}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDeals.map((d) => (
                                <DealRow
                                    key={d.id}
                                    deal={d}
                                    onOpenDeal={() => navigate(dealPath(d.id))}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <StageDiagram
                open={diagramOpen}
                onClose={() => setDiagramOpen(false)}
            />
        </>
    );
};

const EmptyState: React.FC<{ view: View }> = ({ view }) => {
    if (view === "attention") {
        return (
            <div className={classes.empty}>
                Nothing needs you right now. The agents are running.
            </div>
        );
    }
    if (view === "upcoming") {
        return (
            <div className={classes.empty}>
                No upcoming renewals in the next 30 days.
            </div>
        );
    }
    if (view === "closed") {
        return <div className={classes.empty}>No closed deals yet.</div>;
    }
    if (view === "decisions") {
        return (
            <div className={classes.empty}>
                No decisions logged yet. Resolve an item and it will appear here.
            </div>
        );
    }
    return <div className={classes.empty}>No negotiations match.</div>;
};

interface DealRowProps {
    deal: Deal;
    onOpenDeal: () => void;
}

const DealRow: React.FC<DealRowProps> = ({ deal, onOpenDeal }) => (
    <tr onClick={onOpenDeal}>
        <td>
            <div className={classes.supName}>{deal.supplierName}</div>
            <div className={classes.supEmail}>{deal.supplierEmail}</div>
        </td>
        <td className={classes.muted}>{deal.categoryName}</td>
        <td className={classes.mono}>${deal.annualValue.amount.toLocaleString()}</td>
        <td>
            <StatusTag tone={stageTone(deal.state)}>
                {stageKindLabel(deal.state)}
            </StatusTag>
        </td>
        <td>
            {deal.statusChip.label && (
                <StatusTag tone={deal.statusChip.tone}>
                    {deal.statusChip.label}
                </StatusTag>
            )}
        </td>
        <td className={deal.state.kind === "ClosedWon" ? classes.resultWon : classes.muted}>
            {deal.resultLabel}
        </td>
    </tr>
);


const DecisionsTable: React.FC<{
    decisions: DecisionResolution[];
    workspace: Workspace;
    onOpen: (id: string) => void;
}> = ({ decisions, workspace, onOpen }) => (
    <table className={`${classes.activeTable} ${classes.sixColTable}`}>
        <thead>
            <tr>
                <th>Supplier</th>
                <th>Category</th>
                <th>Annual</th>
                <th>Stage</th>
                <th>Trigger</th>
                <th>Decision</th>
            </tr>
        </thead>
        <tbody>
            {decisions.map((d, i) => {
                const deal = d.dealId
                    ? workspace.deals.find((x) => x.id === d.dealId)
                    : undefined;
                return (
                    <tr
                        key={i}
                        onClick={d.dealId ? () => onOpen(d.dealId!) : undefined}
                        style={d.dealId ? undefined : { cursor: "default" }}
                    >
                        <td>
                            <div className={classes.supName}>{d.supplierName}</div>
                            {deal && (
                                <div className={classes.supEmail}>
                                    {deal.supplierEmail}
                                </div>
                            )}
                        </td>
                        <td className={classes.muted}>
                            {deal?.categoryName ?? "—"}
                        </td>
                        <td className={classes.mono}>
                            {deal
                                ? `$${deal.annualValue.amount.toLocaleString()}`
                                : "—"}
                        </td>
                        <td>
                            {deal ? (
                                <StatusTag tone={stageTone(deal.state)}>
                                    {stageKindLabel(deal.state)}
                                </StatusTag>
                            ) : (
                                "—"
                            )}
                        </td>
                        <td className={classes.muted}>{d.issue}</td>
                        <td>
                            <div className={classes.supName}>
                                {d.chosenOptionTitle}
                            </div>
                            <div className={classes.supEmail}>
                                {d.decidedByName} · {d.decidedAtLabel}
                            </div>
                        </td>
                    </tr>
                );
            })}
        </tbody>
    </table>
);
